import { v4 as uuidv4 } from 'uuid';

import {
  deleteScript as _deleteScript,
  deleteScriptByName as _deleteScriptByName,
  getLibraryScriptConfigByName,
  getScript as _getScript,
  getScriptByName as _getScriptByName,
  getScripts as _getScripts,
  putScript as _putScript,
  type ScriptSkeleton,
} from '../api/ScriptApi';
import { type ExportMetaData, ResultCallback } from '../ops/OpsTypes';
import { State } from '../shared/State';
import { decode, encode, isBase64Encoded } from '../utils/Base64Utils';
import {
  createProgressIndicator,
  debugMessage,
  stopProgressIndicator,
  updateProgressIndicator,
  verboseMessage,
} from '../utils/Console';
import {
  convertBase64TextToArray,
  convertTextArrayToBase64,
  getMetadata,
  getResult,
} from '../utils/ExportImportUtils';
import {
  applyNameCollisionPolicy,
  getCurrentRealmName,
} from '../utils/ForgeRockUtils';
import { isScriptValid } from '../utils/ScriptValidationUtils';
import { FrodoError } from './FrodoError';

export type Script = {
  /**
   * Create an empty script export template
   * @returns {ScriptExportInterface} an empty script export template
   */
  createScriptExportTemplate(): ScriptExportInterface;
  /**
   * Read all scripts
   * @returns {Promise<ScriptSkeleton[]>} a promise that resolves to an array of script objects
   */
  readScripts(): Promise<ScriptSkeleton[]>;
  /**
   * Get the names of library scripts required by the input script object
   *
   * @param {ScriptSkeleton} scriptObj the script object
   * @returns an array of required library script names
   */
  getLibraryScriptNames(scriptObj: ScriptSkeleton): string[];
  /**
   * Read script
   * @param {string} scriptId script id
   * @returns {Promise<ScriptSkeleton>} promise that resolves to a script object
   */
  readScript(scriptId: string): Promise<ScriptSkeleton>;
  /**
   * Read script by name
   * @param {string} scriptName name of the script
   * @returns {Promise<ScriptSkeleton>} promise that resolves to a script object
   */
  readScriptByName(scriptName: string): Promise<ScriptSkeleton>;
  /**
   * Create script
   * @param {string} scriptId script id
   * @param {string} scriptName name of the script
   * @param {ScriptSkeleton} scriptData script object
   * @returns {Promise<ScriptSkeleton>} a status object
   */
  createScript(
    scriptId: string,
    scriptName: string,
    scriptData: ScriptSkeleton
  ): Promise<ScriptSkeleton>;
  /**
   * Create or update script
   * @param {string} scriptId script id
   * @param {ScriptSkeleton} scriptData script object
   * @returns {Promise<ScriptSkeleton>} a status object
   */
  updateScript(
    scriptId: string,
    scriptData: ScriptSkeleton
  ): Promise<ScriptSkeleton>;
  /**
   * Delete script
   * @param {string} scriptId script id
   * @returns {Promise<ScriptSkeleton>} promise that resolves to a script object
   */
  deleteScript(scriptId: string): Promise<ScriptSkeleton>;
  /**
   * Delete script by name
   * @param {String} scriptName script name
   * @returns {Promise<ScriptSkeleton>} a promise that resolves to a script object
   */
  deleteScriptByName(scriptName: string): Promise<ScriptSkeleton>;
  /**
   * Delete all non-default scripts
   * @param {ResultCallback} resultCallback Optional callback to process individual results
   * @returns {Promise<ScriptSkeleton[]>} a promise that resolves to an array of script objects
   */
  deleteScripts(
    resultCallback?: ResultCallback<ScriptSkeleton>
  ): Promise<ScriptSkeleton[]>;
  /**
   * Export all scripts
   * @param {ScriptExportOptions} options script export options
   * @param {ResultCallback} resultCallback Optional callback to process individual results
   * @returns {Promise<ScriptExportInterface>} a promise that resolved to a ScriptExportInterface object
   */
  exportScripts(
    options?: ScriptExportOptions,
    resultCallback?: ResultCallback<ScriptSkeleton>
  ): Promise<ScriptExportInterface>;
  /**
   * Export script by id
   * @param {string} scriptId script uuid
   * @param {ScriptExportOptions} options script export options
   * @param {ResultCallback} resultCallback Optional callback to process individual results
   * @returns {Promise<ScriptExportInterface>} a promise that resolved to a ScriptExportInterface object
   */
  exportScript(
    scriptId: string,
    options?: ScriptExportOptions
  ): Promise<ScriptExportInterface>;
  /**
   * Export script by name
   * @param {string} scriptName script name
   * @param {ScriptExportOptions} options script export options
   * @returns {Promise<ScriptExportInterface>} a promise that resolved to a ScriptExportInterface object
   */
  exportScriptByName(
    scriptName: string,
    options?: ScriptExportOptions
  ): Promise<ScriptExportInterface>;
  /**
   * Import scripts
   * @param {string} scriptId Optional id of script. If supplied, only the script of that id is imported. Takes priority over scriptName if both are provided.
   * @param {string} scriptName Optional name of script. If supplied, only the script of that name is imported
   * @param {ScriptExportInterface} importData Script import data
   * @param {ScriptImportOptions} options Script import options
   * @param {boolean} validate If true, validates Javascript scripts to ensure no errors exist in them. Default: false
   * @param {ResultCallback} resultCallback Optional callback to process individual results
   * @returns {Promise<ScriptSkeleton[]>} the imported scripts
   */
  importScripts(
    scriptId: string,
    scriptName: string,
    importData: ScriptExportInterface,
    options?: ScriptImportOptions,
    validate?: boolean,
    resultCallback?: ResultCallback<ScriptSkeleton>
  ): Promise<ScriptSkeleton[]>;
};

export default (state: State): Script => {
  return {
    createScriptExportTemplate(): ScriptExportInterface {
      return createScriptExportTemplate({ state });
    },
    async readScripts(): Promise<ScriptSkeleton[]> {
      return readScripts({ state });
    },
    getLibraryScriptNames(scriptObj: ScriptSkeleton): string[] {
      return getLibraryScriptNames(scriptObj);
    },
    async readScript(scriptId: string): Promise<ScriptSkeleton> {
      return readScript({ scriptId, state });
    },
    async readScriptByName(scriptName: string): Promise<ScriptSkeleton> {
      return readScriptByName({ scriptName, state });
    },
    async createScript(
      scriptId: string,
      scriptName: string,
      scriptData: ScriptSkeleton
    ): Promise<ScriptSkeleton> {
      return createScript({ scriptId, scriptName, scriptData, state });
    },
    async updateScript(
      scriptId: string,
      scriptData: ScriptSkeleton
    ): Promise<ScriptSkeleton> {
      return updateScript({ scriptId, scriptData, state });
    },
    async deleteScript(scriptId: string): Promise<ScriptSkeleton> {
      return deleteScript({ scriptId, state });
    },
    async deleteScriptByName(scriptName: string): Promise<ScriptSkeleton> {
      return deleteScriptByName({ scriptName, state });
    },
    async deleteScripts(resultCallback = void 0): Promise<ScriptSkeleton[]> {
      return deleteScripts({ resultCallback, state });
    },
    async exportScript(
      scriptId: string,
      options: ScriptExportOptions = {
        deps: true,
        includeDefault: true,
        useStringArrays: true,
      }
    ): Promise<ScriptExportInterface> {
      return exportScript({ scriptId, options, state });
    },
    async exportScriptByName(
      scriptName: string,
      options: ScriptExportOptions = {
        deps: true,
        includeDefault: true,
        useStringArrays: true,
      }
    ): Promise<ScriptExportInterface> {
      return exportScriptByName({ scriptName, options, state });
    },
    async exportScripts(
      options: ScriptExportOptions = {
        deps: true,
        includeDefault: false,
        useStringArrays: true,
      },
      resultCallback = void 0
    ): Promise<ScriptExportInterface> {
      return exportScripts({ options, resultCallback, state });
    },
    async importScripts(
      scriptId: string,
      scriptName: string,
      importData: ScriptExportInterface,
      options = {
        deps: true,
        reUuid: false,
        includeDefault: false,
      },
      validate = false,
      resultCallback = void 0
    ): Promise<ScriptSkeleton[]> {
      return importScripts({
        scriptId,
        scriptName,
        importData,
        options,
        validate,
        resultCallback,
        state,
      });
    },
  };
};

export interface ScriptExportInterface {
  meta?: ExportMetaData;
  script: Record<string, ScriptSkeleton>;
}

/**
 * Script import options
 */
export interface ScriptImportOptions {
  /**
   * Include dependency (library) scripts in export
   */
  deps: boolean;
  /**
   * Generate new UUIDs for all scripts during import.
   */
  reUuid: boolean;
  /**
   * Include default scripts in import if true
   */
  includeDefault: boolean;
}

/**
 * Script export options
 */
export interface ScriptExportOptions {
  /**
   * Include dependency (library) scripts in export
   */
  deps: boolean;
  /**
   * Include default scripts in export if true
   */
  includeDefault: boolean;
  /**
   * Use string arrays to store script code
   */
  useStringArrays: boolean;
}

/**
 * Create an empty script export template
 * @returns {ScriptExportInterface} an empty script export template
 */
export function createScriptExportTemplate({
  state,
}: {
  state: State;
}): ScriptExportInterface {
  return {
    meta: getMetadata({ state }),
    script: {},
  } as ScriptExportInterface;
}

/**
 * Get all scripts
 * @returns {Promise<ScriptSkeleton[]>} a promise that resolves to an array of script objects
 */
export async function readScripts({
  state,
}: {
  state: State;
}): Promise<ScriptSkeleton[]> {
  try {
    const { result } = await _getScripts({ state });
    return result;
  } catch (error) {
    throw new FrodoError(
      `Error reading ${getCurrentRealmName(state) + ' realm'} scripts`,
      error
    );
  }
}

/**
 * Get the names of library scripts required by the input script object
 *
 * @param {ScriptSkeleton} scriptObj the script object
 * @returns {string[]} an array of required library script names
 */
export function getLibraryScriptNames(scriptObj: ScriptSkeleton): string[] {
  const script = Array.isArray(scriptObj.script)
    ? scriptObj.script.join('\n')
    : decode(scriptObj.script as string);
  const regex = /require\(['|"](.+?)['|"]\)/g;
  const matches = [...script.matchAll(regex)];
  return matches.map((m) => String(m[1]));
}

/**
 * Gets all library scripts for a given script recursively
 *
 * @param {ScriptSkeleton} scriptData the script object
 * @returns {ScriptSkeleton[]} all the library scripts needed for the given script
 */
export async function getLibraryScripts({
  scriptData,
  state,
}: {
  scriptData: ScriptSkeleton;
  state: State;
}): Promise<ScriptSkeleton[]> {
  const scripts = await getLibraryScriptsRecurse({
    scriptData,
    scripts: {},
    state,
  });
  return Object.values(scripts);
}

/**
 * Recursive helper method for getting library scripts
 *
 * @param {ScriptSkeleton} scriptData the script object
 */
async function getLibraryScriptsRecurse({
  scriptData,
  scripts = {},
  state,
}: {
  scriptData: ScriptSkeleton;
  scripts: Record<string, ScriptSkeleton>;
  state: State;
}): Promise<Record<string, ScriptSkeleton>> {
  const scriptNames = getLibraryScriptNames(scriptData);
  const scriptObjs = await Promise.all(
    scriptNames
      // Filter is necessary here to prevent infinite recursion if there is a circular dependency
      .filter((scriptName) => !(scriptName in scripts))
      .map((scriptName) => readScriptByName({ scriptName, state }))
  );
  for (const scriptObj of scriptObjs) {
    scripts[scriptObj.name] = scriptObj;
  }
  for (const scriptObj of scriptObjs) {
    await getLibraryScriptsRecurse({
      scriptData: scriptObj,
      scripts,
      state,
    });
  }
  return scripts;
}

/**
 * Get script
 *
 * @param {string} scriptId the script id
 * @returns {Promise<ScriptSkeleton>} a promise that resolves to an array of script objects
 */
export async function readScript({
  scriptId,
  state,
}: {
  scriptId: string;
  state: State;
}): Promise<ScriptSkeleton> {
  try {
    return await _getScript({ scriptId, state });
  } catch (error) {
    throw new FrodoError(
      `Error reading ${getCurrentRealmName(state) + ' realm'} script ${scriptId}`,
      error
    );
  }
}

/**
 * Get script by name
 * @param {string} scriptName name of the script
 * @returns {Promise<ScriptSkeleton>} promise that resolves to a script object
 */
export async function readScriptByName({
  scriptName,
  state,
}: {
  scriptName: string;
  state: State;
}): Promise<ScriptSkeleton> {
  try {
    const { result } = await _getScriptByName({ scriptName, state });
    switch (result.length) {
      case 1:
        return result[0];
      case 0:
        throw new FrodoError(
          `${getCurrentRealmName(state) + ' realm'} script '${scriptName}' not found`
        );
      default:
        throw new FrodoError(
          `${result.length} ${getCurrentRealmName(state) + ' realm'} scripts '${scriptName}' found`
        );
    }
  } catch (error) {
    throw new FrodoError(
      `Error reading ${getCurrentRealmName(state) + ' realm'} script ${scriptName}`,
      error
    );
  }
}

/**
 * Create script
 * @param {string} scriptId the script id
 * @param {string} scriptName the script name
 * @param {ScriptSkeleton} scriptData script object
 * @returns {Promise<ScriptSkeleton>} a promise resolving to a script object
 */
export async function createScript({
  scriptId,
  scriptName,
  scriptData,
  state,
}: {
  scriptId: string;
  scriptName: string;
  scriptData: ScriptSkeleton;
  state: State;
}): Promise<ScriptSkeleton> {
  debugMessage({ message: `ScriptOps.createOAuth2Client: start`, state });
  scriptData._id = scriptId;
  scriptData.name = scriptName;
  try {
    await _getScript({ scriptId, state });
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    try {
      const result = await updateScript({
        scriptId,
        scriptData,
        state,
      });
      debugMessage({ message: `ScriptOps.createOAuth2Client: end`, state });
      return result;
    } catch (error) {
      throw new FrodoError(
        `Error creating ${getCurrentRealmName(state) + ' realm'} script`,
        error
      );
    }
  }
  throw new FrodoError(
    `${getCurrentRealmName(state) + ' realm'} script ${scriptData._id} already exists!`
  );
}

/**
 * Create or update script
 * @param {string} scriptId script uuid
 * @param {ScriptSkeleton} scriptData script object
 * @returns {Promise<ScriptSkeleton>} a status object
 */
export async function updateScript({
  scriptId,
  scriptData,
  state,
}: {
  scriptId: string;
  scriptData: ScriptSkeleton;
  state: State;
}): Promise<ScriptSkeleton> {
  let result = null;
  try {
    if (Array.isArray(scriptData.script)) {
      scriptData.script = convertTextArrayToBase64(scriptData.script);
    } else if (!isBase64Encoded(scriptData.script)) {
      scriptData.script = encode(scriptData.script);
    }
    result = await _putScript({ scriptId, scriptData, state });
  } catch (error) {
    if (error.response?.status === 409) {
      verboseMessage({
        message: `createOrUpdateScript WARNING: script with name ${scriptData.name} already exists, using renaming policy... <name> => <name - imported (n)>`,
        state,
      });
      const newName = applyNameCollisionPolicy(scriptData.name);
      scriptData.name = newName;
      result = await updateScript({ scriptId, scriptData, state });
      verboseMessage({
        message: `Saved script as ${newName}`,
        state,
      });
    } else
      throw new FrodoError(
        `Error updating ${getCurrentRealmName(state) + ' realm'} script`,
        error
      );
  }
  return result;
}

/**
 * Delete script
 * @param {string} scriptId script uuid
 * @returns {Promise<ScriptSkeleton>} a promise resolving to a script object
 */
export async function deleteScript({
  scriptId,
  state,
}: {
  scriptId: string;
  state: State;
}): Promise<ScriptSkeleton> {
  try {
    return _deleteScript({ scriptId, state });
  } catch (error) {
    throw new FrodoError(
      `Error deleting ${getCurrentRealmName(state) + ' realm'} script ${scriptId}`,
      error
    );
  }
}

/**
 * Delete script by name
 * @param {String} scriptName script name
 * @returns {Promise<ScriptSkeleton>} a promise that resolves to a script object
 */
export async function deleteScriptByName({
  scriptName,
  state,
}: {
  scriptName: string;
  state: State;
}): Promise<ScriptSkeleton> {
  try {
    return _deleteScriptByName({ scriptName, state });
  } catch (error) {
    throw new FrodoError(
      `Error deleting ${getCurrentRealmName(state) + ' realm'} script ${scriptName}`,
      error
    );
  }
}

/**
 * Delete all non-default scripts
 * @param {ResultCallback} resultCallback Optional callback to process individual results
 * @returns {Promise<ScriptSkeleton[]>} a promise that resolves to an array of script objects
 */
export async function deleteScripts({
  resultCallback = void 0,
  state,
}: {
  resultCallback?: ResultCallback<ScriptSkeleton>;
  state: State;
}): Promise<ScriptSkeleton[]> {
  const result = await readScripts({ state });
  //Unable to delete default scripts, so filter them out
  const scripts = result.filter((s) => !s.default);
  const deletedScripts = [];
  for (const script of scripts) {
    const result: ScriptSkeleton = await getResult(
      resultCallback,
      `Error deleting ${getCurrentRealmName(state) + ' realm'} script ${script.name}`,
      deleteScript,
      {
        scriptId: script._id,
        state,
      }
    );
    if (result) {
      deletedScripts.push(result);
    }
  }
  return deletedScripts;
}

/**
 * Export script by id
 *
 * @param {string} scriptId script uuid
 * @param {ScriptExportOptions} options script export options
 * @returns {Promise<ScriptExportInterface>} a promise that resolved to a ScriptExportInterface object
 */
export async function exportScript({
  scriptId,
  options = {
    deps: true,
    includeDefault: true,
    useStringArrays: true,
  },
  state,
}: {
  scriptId: string;
  options?: ScriptExportOptions;
  state: State;
}): Promise<ScriptExportInterface> {
  try {
    debugMessage({ message: `ScriptOps.exportScriptById: start`, state });
    const scriptData = await readScript({ scriptId, state });
    const exportData = prepareScriptExport({ scriptData, options, state });
    debugMessage({ message: `ScriptOps.exportScriptById: end`, state });
    return exportData;
  } catch (error) {
    throw new FrodoError(
      `Error exporting ${getCurrentRealmName(state) + ' realm'} script ${scriptId}`,
      error
    );
  }
}

/**
 * Export script by name
 *
 * @param {string} scriptName script name
 * @param {ScriptExportOptions} options script export options
 * @returns {Promise<ScriptExportInterface>} a promise that resolved to a ScriptExportInterface object
 */
export async function exportScriptByName({
  scriptName,
  options = {
    deps: true,
    includeDefault: true,
    useStringArrays: true,
  },
  state,
}: {
  scriptName: string;
  options?: ScriptExportOptions;
  state: State;
}): Promise<ScriptExportInterface> {
  try {
    debugMessage({ message: `ScriptOps.exportScriptByName: start`, state });
    const scriptData = await readScriptByName({ scriptName, state });
    const exportData = prepareScriptExport({ scriptData, options, state });
    debugMessage({ message: `ScriptOps.exportScriptByName: end`, state });
    return exportData;
  } catch (error) {
    throw new FrodoError(
      `Error exporting ${getCurrentRealmName(state) + ' realm'} script ${scriptName}`,
      error
    );
  }
}

/**
 * Export all scripts
 *
 * @param {ScriptExportOptions} options script export options
 * @param {ResultCallback} resultCallback Optional callback to process individual results
 * @returns {Promise<ScriptExportInterface>} a promise that resolved to a ScriptExportInterface object
 */
export async function exportScripts({
  options = {
    deps: true,
    includeDefault: false,
    useStringArrays: true,
  },
  resultCallback = void 0,
  state,
}: {
  options?: ScriptExportOptions;
  resultCallback: ResultCallback<ScriptSkeleton>;
  state: State;
}): Promise<ScriptExportInterface> {
  const { includeDefault, useStringArrays } = options;
  let scriptList = await readScripts({ state });
  if (!includeDefault)
    scriptList = scriptList.filter((script) => !script.default);
  const exportData = createScriptExportTemplate({ state });
  const indicatorId = createProgressIndicator({
    total: scriptList.length,
    message: `Exporting ${getCurrentRealmName(state) + ' realm'} ${scriptList.length} scripts...`,
    state,
  });
  for (const scriptData of scriptList) {
    updateProgressIndicator({
      id: indicatorId,
      message: `Reading ${getCurrentRealmName(state) + ' realm'} script ${scriptData.name}`,
      state,
    });
    const result: ScriptSkeleton = await getResult(
      resultCallback,
      `Error exporting ${getCurrentRealmName(state) + ' realm'} script ${scriptData.name}`,
      prepareScriptForExport,
      {
        scriptData,
        useStringArrays,
        state,
      }
    );
    if (result) {
      exportData.script[scriptData._id] = result;
    }
  }
  stopProgressIndicator({
    id: indicatorId,
    message: `Exported ${getCurrentRealmName(state) + ' realm'} ${scriptList.length} scripts.`,
    state,
  });
  return exportData;
}

/**
 * Import scripts
 * @param {object} params Params object.
 * @param {string} params.scriptId Optional id of script. If supplied, only the script of that id is imported. Takes priority over scriptName if both are provided.
 * @param {string} params.scriptName Optional name of script. If supplied, only the script of that name is imported
 * @param {ScriptExportInterface} params.importData Script import data
 * @param {ScriptImportOptions} params.options Script import options
 * @param {boolean} params.validate If true, validates Javascript scripts to ensure no errors exist in them. Default: false
 * @param {ResultCallback} resultCallback Optional callback to process individual results
 * @returns {Promise<ScriptSkeleton[]>} the imported scripts
 */
export async function importScripts({
  scriptId,
  scriptName,
  importData,
  options = {
    deps: true,
    reUuid: false,
    includeDefault: false,
  },
  validate = false,
  resultCallback = void 0,
  state,
}: {
  scriptId?: string;
  scriptName?: string;
  importData: ScriptExportInterface;
  options?: ScriptImportOptions;
  validate?: boolean;
  resultCallback?: ResultCallback<ScriptSkeleton>;
  state: State;
}): Promise<ScriptSkeleton[]> {
  debugMessage({ message: `ScriptOps.importScripts: start`, state });
  const response = [];
  for (const existingId of Object.keys(importData.script)) {
    try {
      const scriptData = importData.script[existingId];
      const isDefault = !options.includeDefault && scriptData.default;
      // Only import script if the scriptName matches the current script. Note this only applies if we are not importing dependencies since if there are dependencies then we want to import all the scripts in the file.
      const shouldNotImportScript =
        !options.deps &&
        ((scriptId && scriptId !== scriptData._id) ||
          (!scriptId && scriptName && scriptName !== scriptData.name));
      if (isDefault || shouldNotImportScript) continue;
      debugMessage({
        message: `ScriptOps.importScripts: Importing script ${scriptData.name} (${existingId})`,
        state,
      });
      let newId = existingId;
      if (options.reUuid) {
        newId = uuidv4();
        debugMessage({
          message: `ScriptOps.importScripts: Re-uuid-ing script ${scriptData.name} ${existingId} => ${newId}...`,
          state,
        });
        scriptData._id = newId;
      }
      if (validate) {
        if (!isScriptValid({ scriptData, state })) {
          throw new FrodoError(
            `${getCurrentRealmName(state) + ' realm'} script ${newId} is invalid`
          );
        }
      }
      const result = await updateScript({
        scriptId: newId,
        scriptData,
        state,
      });
      if (resultCallback) {
        resultCallback(undefined, result);
      }
      response.push(result);
    } catch (e) {
      if (resultCallback) {
        resultCallback(e, undefined);
      } else {
        throw new FrodoError(
          `Error importing ${getCurrentRealmName(state) + ' realm'} script '${importData.script[existingId].name}'`,
          e
        );
      }
    }
  }
  debugMessage({ message: `ScriptOps.importScripts: end`, state });
  return response;
}

/**
 * Helper to export a single script (along with any library scripts if applicable)
 *
 * @param {ScriptSkeleton} scriptData The script data to export
 * @param {ScriptExportOptions} options script export options
 * @returns {Promise<ScriptExportInterface>} the script export
 */
async function prepareScriptExport({
  scriptData,
  options = {
    deps: true,
    includeDefault: true,
    useStringArrays: true,
  },
  state,
}: {
  scriptData: ScriptSkeleton;
  options?: ScriptExportOptions;
  state: State;
}): Promise<ScriptExportInterface> {
  const { deps, useStringArrays } = options;
  const exportData = createScriptExportTemplate({ state });
  exportData.script[scriptData._id] = await prepareScriptForExport({
    scriptData,
    useStringArrays,
    state,
  });
  // handle library scripts
  if (deps) {
    for (const script of await getLibraryScripts({ scriptData, state })) {
      exportData.script[script._id] = await prepareScriptForExport({
        scriptData: script,
        useStringArrays,
        state,
      });
    }
  }
  return exportData;
}

/**
 * Helper to prepare a single script for export
 *
 * @param {ScriptSkeleton} scriptData the script data to prepare for export
 * @param {ScriptExportOptions} options script export options
 * @returns {ScriptSkeleton} the prepared script data
 */
async function prepareScriptForExport({
  scriptData,
  useStringArrays,
  state,
}: {
  scriptData: ScriptSkeleton;
  useStringArrays: boolean;
  state: State;
}): Promise<ScriptSkeleton> {
  scriptData.script = convertBase64TextToArray(scriptData.script as string);
  if (!useStringArrays) {
    scriptData.script = scriptData.script.join('\n');
  }
  if (scriptData.context === 'LIBRARY') {
    const { result } = await getLibraryScriptConfigByName({
      scriptName: scriptData.name,
      state,
    });
    const config = result.find((s) => s._id === scriptData._id);
    return {
      ...scriptData,
      ...config,
    };
  }
  return scriptData;
}
